import { lazy, Suspense, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useAuthStore } from '@auth/authStore';
import { useAsyncResource, useRegisterFilters, useUrlAssetScope } from '@hooks';
import { getAssets, getComponents, getModel } from '@entities';
import { FilterBar, LoadingScreen } from '@components';
import { filterDefinitions, getSearchText } from '@/entities/anomaly/anomaly.filters';
import styles from './AnomalyViewer.module.css';

const ThreeViewerProvider = lazy(() =>
  import('@/shared/components/ThreeViewer/ThreeViewerContext')
    .then(module => ({ default: module.ThreeViewerProvider })));
const ViewerContent = lazy(() =>
  import('./ViewerContent').then(module => ({ default: module.ViewerContent })));

function SelectedAssetViewer({ companyId, siteId, assetId, assetType, data, isActive }) {
  const [searchParams] = useSearchParams();
  const initialComponentKey = searchParams.get('viewerComponent') || '';
  const forceModelRefresh = useRef(false);
  const enabled = Boolean(companyId && siteId && assetId && assetType);
  const model = useAsyncResource(
    () => {
      const forceRefresh = forceModelRefresh.current;
      forceModelRefresh.current = false;
      return getModel(companyId, siteId, assetType, { forceRefresh });
    },
    [companyId, siteId, assetType],
    { initialData: null, enabled }
  );
  const components = useAsyncResource(
    async () => {
      const rows = await getComponents({ companyId, siteId, assetId });
      if (!Array.isArray(rows)) throw new Error('The component API must return a list.');
      return rows;
    },
    [companyId, siteId, assetId],
    { initialData: [], enabled }
  );

  function retryModel() {
    forceModelRefresh.current = true;
    model.reload();
  }

  if (!assetType) return <p className={styles.message}>No model type is recorded for this asset.</p>;
  if (model.isLoading || components.isLoading) return <LoadingScreen />;
  if (model.error || components.error) {
    return (
      <div className={styles.message}>
        <p role="alert">{model.error || components.error}</p>
        <button type="button" className={styles.button} onClick={() => {
          retryModel();
          components.reload();
        }}>Retry loading asset</button>
      </div>
    );
  }
  const modelUrl = typeof model.data === 'string' ? model.data : model.data?.url;
  if (!modelUrl) return <p className={styles.message}>No model is available for this asset.</p>;

  return (
    <Suspense fallback={<LoadingScreen />}>
      <ThreeViewerProvider key={JSON.stringify([modelUrl, initialComponentKey])}>
        <ViewerContent
          key={initialComponentKey || 'interactive'}
          initialComponentKey={initialComponentKey}
          anomalyData={data}
          componentData={components.data}
          objUrl={modelUrl}
          isActive={isActive}
          onModelRetry={retryModel}
        />
      </ThreeViewerProvider>
    </Suspense>
  );
}

export default function AnomalyViewer({ data = [], isActive = true }) {
  const companyId = useAuthStore(state => state.companyID);
  const { siteId, assetId } = useUrlAssetScope();
  const filters = useRegisterFilters({
    namespace: 'anomalies',
    items: data,
    definitions: filterDefinitions,
    getSearchText,
  });

  const assets = useAsyncResource(
    async () => {
      const rows = await getAssets(companyId);
      if (!Array.isArray(rows)) throw new Error('The asset API must return a list.');
      return { companyId, rows };
    },
    [companyId],
    { initialData: null, enabled: Boolean(companyId) }
  );

  const rows = assets.data?.companyId === companyId ? assets.data.rows : [];
  const selectedAsset = rows.find(row =>
    String(row.site_id) === siteId && String(row.asset_id) === assetId
  );
  const assetType = selectedAsset?.asset_type ?? selectedAsset?.AssetType;

  function renderWorkspace() {
    if (!siteId || !assetId) {
      return <p className={styles.message}>Select a site and asset in the scope bar to open its model.</p>;
    }
    if (assets.isLoading) return <LoadingScreen />;
    if (assets.error) {
      return (
        <div className={styles.message}>
          <p role="alert">{assets.error}</p>
          <button type="button" className={styles.button} onClick={() => assets.reload()}>Retry</button>
        </div>
      );
    }
    if (!selectedAsset) {
      return <p className={styles.message}>The selected asset is unavailable in this site.</p>;
    }
    return (
      <SelectedAssetViewer
        key={JSON.stringify([companyId, siteId, assetId, assetType])}
        companyId={companyId}
        siteId={siteId}
        assetId={assetId}
        assetType={assetType}
        data={filters.filtered}
        isActive={isActive}
      />
    );
  }

  return (
    <div className={styles.page}>
      <FilterBar filters={filters} noun="anomalies" />
      <div className={styles.stage}>{renderWorkspace()}</div>
    </div>
  );
}